// Imports
import { Info, Launch, Reviews, StarOutlineRounded, StarRounded } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Slide, Snackbar, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { GobikesApi } from '../../config/Apis';
import { parseErrorForAxios, toIstDateString } from '../utils/CommonUtils';
import { getAuthCookie } from '../auth/AuthUtil';
import { DEFAULT_LOADER_STYLES, NavTabLinks } from '../../config/Constants';
import { ReviewModel, ReviewStates } from '../utils/Models';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { setNavData } from '../../features/navSelection';
import { useDispatch, useSelector } from 'react-redux';
import { getHubSelection, setHubSelection } from '../../features/hubSelection';
import { getVendorHubs } from '../../features/vendorProfile';
import { DataGrid } from '@mui/x-data-grid';
import './Reviews.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// Types and constants
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  toast?: string;
}
interface ReplyModalState {
  open: boolean;
  reviewId?: number;
  orderId?: number;
  userMessage?: string;
  replyText?: string;
  submitting?: boolean;
}
const ratingToNameMap: { [k in keyof Partial<ReviewModel>]: string } = {
  // overallRating: 'Overall Rating',
  valueForMoney: 'Value for money',
  cleanliness: 'Bike condition',
  rentalDeskService: 'Dealer behaviour',
  pickupProcess: 'Pickup process',
  dropProcess: 'Drop process'
}

// Main app component
export function ReviewDashboard(props: {
  window?: () => Window;
  drawerWidth: number
}) {
  // Local state
  const [state, setState] = useState<LocalState>({});
  const [reviewData, setReviewData] = useState<{ reviews: ReviewModel[] }>({ reviews: [] });
  const [replyModal, setReplyModal] = useState<ReplyModalState>({ open: false });

  // Global state
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);
  const vendorHubs = useSelector(getVendorHubs);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pendingReplyId = searchParams.get('replyReviewId') ? Number(searchParams.get('replyReviewId')) : null;
  const pendingHubId = searchParams.get('hubId') ? Number(searchParams.get('hubId')) : null;

  // Load reviews if required on first load
  useEffect(() => {
    const authCookie = getAuthCookie();
    if (!authCookie) return;

    // If the reply link targets a different hub, switch to it first; the effect will re-run
    if (pendingHubId && selectedHub.id !== pendingHubId) {
      const targetHub = vendorHubs?.find(h => h.id === pendingHubId);
      if (targetHub) {
        dispatch(setHubSelection(targetHub));
        return;
      }
    }

    setState({ loading: true });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${selectedHub.id}/reviews`, { headers: { 'vendor-token': authCookie } })
        setState({});
        setReviewData(data.data);
        if (pendingReplyId) {
          const match = (data.data.reviews as ReviewModel[]).find(r => r.id === pendingReplyId);
          if (match) {
            setReplyModal({ open: true, reviewId: match.id, orderId: match.orderId, userMessage: match.message, replyText: match.vendorReply || '' });
            navigate('/reviews', { replace: true });
          }
        }
      } catch (e) {
        setState({ error: true, message: parseErrorForAxios(e) });
      }
    })();
  }, [selectedHub]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading view
  let mainContent = <div></div>;
  if (state.loading) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (state.error) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Default view
  else {
    const ratingWidth = isPhone ? 100 : 200;
    mainContent = <Fragment>
      <Box
        className='gbk-tabs no-boundary'
        sx={{
          typography: 'body1',
          m: '30px 0 0 0',
          height: isPhone ? 'calc(100% - 36px)' : 'calc(100% - 120px)',
          width: 'calc(100% + 10px)',
          marginLeft: '-10px'
        }}
      >
        <DataGrid
          sx={{ display: 'flex', padding: isPhone ? '12px' : 0, paddingRight: 0 }}
          disableColumnMenu={true}
          disableColumnFilter={true}
          className="gbk-dg review-table phone"
          // rowHeight={300}
          disableVirtualization={true}
          rows={reviewData.reviews || []}
          columns={[]}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          headerHeight={0}
          components={{
            Row: (data: { row: ReviewModel }) => (
              <Box className='review-card'>

                {/* Left info box */}
                <Box className='left-info vat'>

                  {/* Left box header */}
                  <div className='review-header'>
                    <Link to={NavTabLinks.BOOKINGS}>
                      <div
                        className='float-left bold'
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          dispatch(setNavData({ orderId: data.row.orderId }));
                        }}
                      >
                        Order ID {data.row.orderId}&nbsp;
                        <span className='gbk-icon-span fs-16' style={{ verticalAlign: 'middle' }}><Launch /></span>
                      </div>
                    </Link>
                    <div className={`${isPhone ? 'float-left fs-12 clear-both' : 'float-right'}`}>
                      {toIstDateString(new Date(data.row.createdAt).getTime())}
                    </div>
                    <div>&nbsp;</div>
                  </div>

                  {/* Left box body */}
                  <div className='review-body' >
                    {Object.keys(ratingToNameMap).map(f => (
                      <div key={f}>

                        {/* Rating name */}
                        <div className="inb vat"
                          style={{
                            width: ratingWidth,
                            fontSize: isPhone ? '12px' : '14px',
                            lineHeight: isPhone ? '18px' : '30px'
                          }}
                        >
                          {ratingToNameMap[f as keyof ReviewModel]}
                        </div>

                        {/* Stars */}
                        <div className="inb vat" style={{ width: ratingWidth }}>
                          {
                            Array.from(Array(Math.floor(Number(data.row[f as keyof ReviewModel]))).keys()).map((_, i) =>
                              <span key={i} className={`gbk-icon-span ${isPhone ? 'fs-16' : 'large'} `}>
                                <StarRounded className="rating-star-green" />
                              </span>)
                          }
                          {
                            Array.from(Array(5 - Math.floor(Number(data.row[f as keyof ReviewModel]))).keys()).map((_, i) =>
                              <span key={i} className={`gbk-icon-span ${isPhone ? 'fs-16' : 'large'} `}>
                                <StarOutlineRounded className="primary-text" />
                              </span>)}
                        </div>
                      </div>
                    ))}
                    <br />
                    <div className={`${isPhone ? 'fs-12' : 'fs-14'}`}>
                      <b>Comments:</b> <br />
                      <span className={`${data.row.message ? '' : 'grey-text-dark'}`}>{data.row.message || 'No comments'}</span>
                    </div>
                    {data.row.vendorReply && (
                      <div className={`${isPhone ? 'fs-12' : 'fs-14'}`} style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#f0f7e6', borderLeft: '3px solid #9c3', borderRadius: '4px' }}>
                        <b>Your reply:</b> <br />
                        <span>{data.row.vendorReply}</span>
                      </div>
                    )}
                    {data.row.state === ReviewStates.ABUSIVE ? (
                      <Fragment>
                        <br />
                        <div className={`${isPhone ? 'fs-12' : 'fs-14'}`}>
                          <span className='grey-text-medium' style={{ lineHeight: '22px' }} >
                            <span className='gbk-icon-span'><Info /></span>
                            &nbsp;Comment will not be public.
                          </span>
                        </div>
                      </Fragment>
                    ) : ''}
                  </div>
                </Box>

                {/* Right info box */}
                <Box className='right-info float-right vat tac'>

                  {/* Ask review button */}
                  <div
                    className='ask-review-button float-right'
                    onClick={async () => {
                      setState({ loading: true });
                      try {
                        await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${data.row.orderId}/request-review`, { userId: data.row.userId }, { headers: { 'vendor-token': authCookie } });
                        setState({ loading: false, toast: 'SMS and mail sent to customer!' });
                      } catch (e: any) {
                        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                        setState({ message: errorMessage, toast: errorMessage })
                      }
                    }}
                  >
                    <b>
                      <span className='gbk-icon-span'><Reviews /></span> &nbsp;
                      <span className='fs-12'>Ask for review again</span>
                    </b>
                  </div>

                  {/* Review tag */}
                  <div className='review-tag float-right'>
                    {data.row.state}
                  </div>

                  {/* Overall rating value */}
                  <div className='overall-rating clear-both inb'>
                    <span className={`gbk-icon-span ${isPhone ? 'fs-32' : 'fs-40'}`} style={{ verticalAlign: 'middle' }}><StarRounded /></span>
                    <span
                      className={`${isPhone ? 'fs-18' : ''}`}
                      style={{ verticalAlign: 'top', lineHeight: isPhone ? '40px' : '48px' }}
                    >
                      {data.row.overallRating}
                    </span>
                  </div>
                  <br />
                  <div
                    className='fs-16 grey-text-dark roboto'
                    style={{
                      paddingTop: '10px',
                      verticalAlign: 'top',
                      lineHeight: isPhone ? '10px' : '30px',
                      fontSize: isPhone ? '12px' : '16px'
                    }}>
                    Overall rating
                  </div>
                  {data.row.message && (
                    <div
                      className='ask-review-button float-right'
                      style={{ marginTop: '10px', backgroundColor: data.row.vendorReply ? '#ddd' : '#bbdd77' }}
                      onClick={() => {
                        setReplyModal({
                          open: true,
                          reviewId: data.row.id,
                          orderId: data.row.orderId,
                          userMessage: data.row.message,
                          replyText: data.row.vendorReply || ''
                        });
                      }}
                    >
                      <b>
                        <span className='fs-12'>{data.row.vendorReply ? 'Edit Reply' : 'Reply'}</span>
                      </b>
                    </div>
                  )}
                </Box>
              </Box>
            )
          }}
        />
      </Box >
    </Fragment>
  }

  // Handle reply submit
  const handleReplySubmit = async () => {
    if (!replyModal.reviewId || !replyModal.replyText?.trim()) return;
    setReplyModal(prev => ({ ...prev, submitting: true }));
    try {
      await GobikesApi.post(
        `/vendors/reviews/${replyModal.reviewId}/reply`,
        { vendorReply: replyModal.replyText },
        { headers: { 'vendor-token': authCookie } }
      );
      setReviewData(prev => ({
        reviews: prev.reviews.map(r =>
          r.id === replyModal.reviewId ? { ...r, vendorReply: replyModal.replyText } : r
        )
      }));
      setReplyModal({ open: false });
      setState({ toast: 'Reply submitted!' });
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
      setReplyModal(prev => ({ ...prev, submitting: false }));
      setState({ toast: errorMessage });
    }
  };

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className='gbk-tabcontent'>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div className={`tab-header-fab`} >
            <Reviews fontSize="large" />
          </div>

          {/* Text */}
          <Typography>
            User Reviews
          </Typography>
        </Box>

        {/* Toast */}
        <Snackbar
          open={Boolean(state.toast)}
          message={state.toast}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: isPhone ? 'bottom' : 'top', horizontal: isPhone ? 'center' : 'right' }}
          sx={{ margin: isPhone ? '80px 50px' : '' }}
          TransitionComponent={(props) => (
            <Slide {...props} direction={isPhone ? 'up' : 'left'} />
          )}
          onClose={
            (_, reason?: string) => {
              if (reason === 'clickaway') {
                return;
              }
              setState({ ...state, toast: '' })
            }
          }
        />

        {/* Main content */}
        {mainContent}

        {/* Reply Modal */}
        <Dialog open={replyModal.open} onClose={() => setReplyModal({ open: false })} fullWidth maxWidth="sm">
          <DialogTitle>Reply to Review</DialogTitle>
          <DialogContent>
            {replyModal.userMessage ? (
              <div style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '14px' }}>
                <b>Customer comment:</b><br />
                <span>{replyModal.userMessage}</span>
              </div>
            ) : (
              <div style={{ marginBottom: '16px', color: '#888', fontSize: '14px' }}>
                <i>No comment from customer.</i>
              </div>
            )}
            <TextField
              label="Your reply"
              multiline
              minRows={3}
              fullWidth
              value={replyModal.replyText || ''}
              onChange={e => setReplyModal(prev => ({ ...prev, replyText: e.target.value }))}
              disabled={replyModal.submitting}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReplyModal({ open: false })} disabled={replyModal.submitting}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleReplySubmit}
              disabled={replyModal.submitting || !replyModal.replyText?.trim()}
            >
              {replyModal.submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

      </div >
    </Box >
  )

}